import { format, parseISO, eachDayOfInterval, isWeekend, addDays, isValid } from 'date-fns';

/**
 * Détails d'un jour travaillé
 */
export interface JourDetail {
  date: string;
  period: 'AM' | 'PM' | 'FULL';
  value?: number;
}

/**
 * Interface minimale pour les objets contenant des heures d'affectation
 */
export interface AffectationHeures {
  heures_par_jour?: number | null;
  jours_details?: JourDetail[] | null;
}

/**
 * Nombre d'heures par jour par défaut (BTP standard)
 */
export const DEFAULT_HOURS_PER_DAY = 8;

/**
 * Calcule les heures travaillées pour une affectation sur un jour donné.
 * Gère les demi-journées (AM/PM) et journées complètes (FULL ou AM+PM).
 *
 * @param affectation - L'affectation contenant heures_par_jour et jours_details
 * @param day - La date (objet Date ou string YYYY-MM-DD)
 * @returns Le nombre d'heures travaillées ce jour
 */
export function getHoursForDay(
  affectation: AffectationHeures,
  day: Date | string
): number {
  const dateStr = typeof day === 'string' ? day : format(day, 'yyyy-MM-dd');
  const baseHours = affectation.heures_par_jour ?? DEFAULT_HOURS_PER_DAY;

  const details = affectation.jours_details;
  if (!details || details.length === 0) {
    return baseHours;
  }

  const dayDetails = details.filter(jd => jd.date === dateStr);
  if (dayDetails.length === 0) {
    return baseHours;
  }

  const hasAM = dayDetails.some(d => d.period === 'AM');
  const hasPM = dayDetails.some(d => d.period === 'PM');
  const hasFull = dayDetails.some(d => d.period === 'FULL');

  if ((hasAM && hasPM) || hasFull) {
    return baseHours;
  }
  return baseHours / 2;
}

/**
 * Formate les heures pour affichage UI.
 * Ex: "8h", "4h (mat.)", "4h (ap-m.)"
 *
 * @param affectation - L'affectation contenant heures_par_jour et jours_details
 * @param day - La date (objet Date ou string YYYY-MM-DD)
 * @returns Chaîne formatée pour l'affichage
 */
export function formatHoursForDay(
  affectation: AffectationHeures,
  day: Date | string
): string {
  const dateStr = typeof day === 'string' ? day : format(day, 'yyyy-MM-dd');
  const baseHours = affectation.heures_par_jour ?? DEFAULT_HOURS_PER_DAY;

  const details = affectation.jours_details;
  if (!details || details.length === 0) {
    return `${baseHours}h`;
  }

  const dayDetails = details.filter(jd => jd.date === dateStr);
  if (dayDetails.length === 0) {
    return `${baseHours}h`;
  }

  const hasAM = dayDetails.some(d => d.period === 'AM');
  const hasPM = dayDetails.some(d => d.period === 'PM');
  const hasFull = dayDetails.some(d => d.period === 'FULL');

  if ((hasAM && hasPM) || hasFull) {
    return `${baseHours}h`;
  }
  if (hasAM) {
    return `${baseHours / 2}h (mat.)`;
  }
  return `${baseHours / 2}h (ap-m.)`;
}

/**
 * Options pour le calcul des jours ouvrés
 */
export interface CalculateJoursOuvresOptions {
  /**
   * Si true, inclut le dernier jour même s'il tombe un weekend
   * Utile quand l'utilisateur veut explicitement que le travail finisse un samedi
   */
  inclureWeekendFin?: boolean;
}

/**
 * Calcule le nombre de jours ouvrés (lundi-vendredi) entre deux dates incluses.
 * Les weekends (samedi et dimanche) sont exclus par défaut.
 *
 * @param dateDebut - Date de début (Date ou string YYYY-MM-DD)
 * @param dateFin - Date de fin (Date ou string YYYY-MM-DD)
 * @param optionsOrInclureWeekend - Options de calcul ou boolean pour inclureWeekendFin
 * @returns Nombre de jours ouvrés
 */
export function calculateJoursOuvres(
  dateDebut: Date | string,
  dateFin: Date | string,
  optionsOrInclureWeekend: CalculateJoursOuvresOptions | boolean = {}
): number {
  // Supporter le 3ème argument comme boolean pour compatibilité
  const inclureWeekendFin = typeof optionsOrInclureWeekend === 'boolean'
    ? optionsOrInclureWeekend
    : optionsOrInclureWeekend.inclureWeekendFin ?? false;

  try {
    // Normaliser les dates en objets Date
    const debut = typeof dateDebut === 'string' ? parseISO(dateDebut) : new Date(dateDebut);
    const fin = typeof dateFin === 'string' ? parseISO(dateFin) : new Date(dateFin);

    // Normaliser à minuit pour éviter les problèmes d'heures
    debut.setHours(0, 0, 0, 0);
    fin.setHours(0, 0, 0, 0);

    if (fin < debut) return 0;

    const jours = eachDayOfInterval({ start: debut, end: fin });

    return jours.filter((jour, index) => {
      const isLastDay = index === jours.length - 1;

      // Si c'est un jour de semaine, le compter
      if (!isWeekend(jour)) {
        return true;
      }

      // Si c'est le dernier jour et qu'on inclut le weekend, le compter
      if (isLastDay && inclureWeekendFin) {
        return true;
      }

      return false;
    }).length;
  } catch {
    return 0;
  }
}

/**
 * Ajoute N jours ouvrés (lundi-vendredi) à une date.
 * Ignore les weekends dans le comptage.
 *
 * @param startDate - Date de départ (Date ou string YYYY-MM-DD)
 * @param days - Nombre de jours ouvrés à ajouter
 * @returns Date résultante après ajout des jours ouvrés, ou null si date invalide
 */
export function addBusinessDays(
  startDate: Date | string,
  days: number
): Date | null {
  try {
    let current = typeof startDate === 'string' ? parseISO(startDate) : new Date(startDate);

    if (!isValid(current)) return null;
    if (days <= 0) return current;

    let addedDays = 0;

    while (addedDays < days) {
      current = addDays(current, 1);
      if (!isWeekend(current)) {
        addedDays++;
      }
    }

    return current;
  } catch {
    return null;
  }
}

// Alias français pour compatibilité avec le code existant
export { calculateJoursOuvres as calculerJoursOuvres };
